/**
 * ─── Calendar keyboard SPEC — the single source of truth ────────────────────
 *
 * One entry per keyboard capability the practice's calendar surface promises,
 * REGARDLESS of which engine renders it. Two engines currently coexist:
 *
 *   - "fullcalendar" — the production calendar (molecules/Calendar/Calendar.tsx,
 *     bindings in ./calendarHotkeys.ts + raw listeners in CellNavigation.ts /
 *     EventShelf.tsx)
 *   - "mobili" — the drop-in replacement being built (molecules/MobiliCalendar,
 *     keyboard in useGridKeyboard.ts), see docs/calendar-drop-in-replacement-plan.md
 *
 * HOW THE PAPER TRAIL WORKS: every engine declares a
 * `KeyboardConformance` — a Record over ALL action ids. Adding an entry here
 * therefore breaks BOTH declarations at compile time until each engine
 * explicitly marks the new action ("implemented" / "todo" / …). Refining
 * behavior of an existing action? Update its `behavior` text here and check the
 * other engine's status still tells the truth (downgrade to "partial"/"todo"
 * with a note when it doesn't).
 *
 * Conformance declarations live WITH their engine:
 *   - fullcalendar → ./calendarHotkeys.ts   (fullCalendarKeyboardConformance)
 *   - mobili       → molecules/MobiliCalendar/keyboardConformance.ts
 *
 * `description` is the user-facing German string (feeds the "?" help overlay
 * via the binding table); `behavior` is the engineering contract in English.
 * This file is pure data — no logic, no imports.
 */

export type KeyboardActionSpec = {
  /** Display form of the key(s), e.g. "b", "Shift+J / Shift+L". */
  keys: string;
  /** User-facing German description (help overlay / binding table). */
  description: string;
  /** Engineering contract: what an engine must do to claim "implemented". */
  behavior: string;
};

export const CALENDAR_KEYBOARD_SPEC = {
  cellMove: {
    keys: "↑ ↓ ← → / i k j l",
    description: "Zellencursor bewegen (Pfeiltasten oder i/k/j/l)",
    behavior:
      "Move the cell cursor over the 5-min grid. Arrows AND the ijkl triangle (i up, k down, " +
      "j left, l right; unshifted). Vertical single-steps skip over the event under the cursor; " +
      "horizontal moves preserve the row the event was entered from. Never intercept typing " +
      "contexts or open dialogs.",
  },
  cellJump: {
    keys: "Shift+i / Shift+k",
    description: "Zellencursor 10 Zellen nach oben / unten springen",
    behavior: "Jump the cell cursor 10 rows (50 min) up/down, clamped to the grid; no event-skip.",
  },
  datePage: {
    keys: "Shift+J / Shift+L",
    description: "Kalender: eine Seite zurück / vor (Tag)",
    behavior:
      "Page the displayed date one day back/forward, mirroring the toolbar buttons " +
      "(incl. weekend skipping; 14 days in single-therapist view).",
  },
  belegen: {
    keys: "b",
    description: "Belegen: Regal-Auswahl einplanen, sonst Termin unter dem Cursor bearbeiten",
    behavior:
      "With a shelf selection (Vorlage / Offene-Termine card / selected Ablage pin): plan it at " +
      "the cursor cell through the same validation pipeline as drag-and-drop, with selection " +
      "continuity (template stays selected, group card advances, pinboard drains). Without a " +
      "selection: open the edit dialog for the appointment under the cursor.",
  },
  umziehen: {
    keys: "v / Shift+V",
    description:
      "Verschieben: Termin unter dem Cursor ins Regal heben bzw. aus dem Regal an der Cursor-Position einplanen",
    behavior:
      "The move verb, independent of the b-selection: on a patient appointment GRAB it onto the " +
      "pinboard WITHOUT disturbing the current shelf selection — plain v the WHOLE appointment, " +
      "Shift+V only the slot under the cursor (\"Nur diese\"; the context menu offers the same " +
      "choice as its Alle/Nur-diese dialog). On an empty cell either combo PLACES the most " +
      "recently pinned item (LIFO). Grab+place must be interleavable with b (grab, place " +
      "b-selection, then finalize the move — and vice versa).",
  },
  quickCreate: {
    keys: "f",
    description: "Neues Ereignis an der Cursor-Position anlegen",
    behavior: "Open the quick-create (AddEventModal) pre-filled for the focused cell.",
  },
  markAttended: {
    keys: "w",
    description: "Wahrnehmen: Termin unter dem Cursor als wahrgenommen markieren",
    behavior: "Toggle attended on the appointment under the cursor.",
  },
  openPrescriptions: {
    keys: "r",
    description: "Rezept des Termins unter dem Cursor öffnen",
    behavior: "Navigate to the cursor appointment's patient prescriptions tab.",
  },
  openPatientAppointments: {
    keys: "p",
    description: "Termine des Patienten unter dem Cursor öffnen",
    behavior: "Navigate to the cursor appointment's patient appointments tab.",
  },
  openOverview: {
    keys: "ü",
    description: "Terminüberblick des Termins unter dem Cursor öffnen",
    behavior: "Open the Terminüberblick modal for the cursor appointment.",
  },
  openNotes: {
    keys: "n",
    description: "Notizen zum Termin unter dem Cursor öffnen",
    behavior: "Open the notes dialog for the cursor appointment.",
  },
  cancelAppointment: {
    keys: "x",
    description: "Termin unter dem Cursor absagen",
    behavior: "Start the cancellation flow for the appointment under the cursor.",
  },
  cycleShelfSelection: {
    keys: "a",
    description: "Regal-Auswahl durchschalten: jede Vorlage, jeder offene Termin, jede Ablage",
    behavior:
      "Cycle the shelf selection across EVERY selectable element individually (each template " +
      "handle → each Offene-Termine group card → each Ablage pin), wrapping, mapping a mid-group " +
      "selection back to its card.",
  },
  togglePatientTab: {
    keys: "q",
    description: "Zwischen primärem und sekundärem Patienten wechseln",
    behavior:
      "Toggle the drawer's active patient between the primary (target) and the secondary tab; " +
      "no-op when there is no secondary patient.",
  },
  escapeStagedClear: {
    keys: "Escape",
    description:
      "Stufenweise abwählen: Ablage (Verschieben abbrechen) → zweiter Patient → Regal-Auswahl",
    behavior:
      "Staged clear, one stage per press: 1) cancel the client-side Umziehen pinboard (restores " +
      "lifted events), 2) clear the secondary patient (back to the target tab), 3) clear the " +
      "shelf selection. Never steal Escape from open dialogs/menus/popovers. Provided by the " +
      "shared EventShelf, so engines rendering it inherit this for free — but engine-internal " +
      "Escape handling must not double-fire against it.",
  },
} as const satisfies Record<string, KeyboardActionSpec>;

export type CalendarKeyboardActionId = keyof typeof CALENDAR_KEYBOARD_SPEC;

/**
 * - "implemented": engine fulfils the `behavior` contract itself.
 * - "inherited":  fulfilled by a shared component the engine renders (e.g. EventShelf).
 * - "partial":    some of the contract works — note MUST say what's missing.
 * - "todo":       tracked, not yet built. The compile error you just fixed by
 *                 adding this line IS the paper trail.
 * - "not-applicable": the action can't apply to this engine — note MUST say why.
 */
export type KeyboardConformanceStatus =
  | "implemented"
  | "inherited"
  | "partial"
  | "todo"
  | "not-applicable";

export type KeyboardConformance = Record<
  CalendarKeyboardActionId,
  KeyboardConformanceStatus | { status: KeyboardConformanceStatus; note: string }
>;

/** Normalize a conformance entry to its status (helper for tests/reports). */
export const conformanceStatusOf = (
  entry: KeyboardConformance[CalendarKeyboardActionId]
): KeyboardConformanceStatus => (typeof entry === "string" ? entry : entry.status);

/**
 * ENGINE EXTENSION — a keyboard capability one engine offers BEYOND the
 * cross-engine contract above (e.g. MobiliCalendar's Shift+Arrow range
 * multi-select). Extensions are deliberately non-contractual: the other
 * engine owes them nothing, no conformance entry exists, and they are NOT
 * backported. Each engine documents its extensions next to its conformance
 * record so the split between "contract" and "engine-local" stays explicit.
 * Promote an extension into CALENDAR_KEYBOARD_SPEC (id + behavior + both
 * engines' conformance) the moment it becomes a product requirement for the
 * calendar surface as such.
 */
export type EngineKeyboardExtension = {
  keys: string;
  description: string;
  /** Why this stays engine-local (or what promoting it would take). */
  rationale: string;
};
